import { useState } from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { GridColDef } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { CommonDatagrid } from '..';
import { NavigationAuditRegexService } from '../../../api/services/navigationAuditRegexService';
import {
  addMapping,
  removeMapping,
  updateMapping,
} from '../../../store/Reducers/NavigationAuditRegexSlice';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { INavigationAuditRegexResponse } from '../../../types/api/response/navigationauditregexresponse';
import { IRootState } from '../../../types/store';
import { NavigationAuditRegexFormDialog } from './NavigationAuditRegexFormDialog';
import { CommonDialog, CommonDialogHeader } from '../../Dialog';

export const NavigationAuditRegexDatagrid = () => {
  const dispatch = useDispatch();
  const { mappings } = useSelector((state: IRootState) => state.navigationAuditRegex);
  const navigations = useSelector((state: IRootState) => state.navigations.content);

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<INavigationAuditRegexResponse | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<INavigationAuditRegexResponse | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const onClickEdit = (row: INavigationAuditRegexResponse) => {
    setEditTarget(row);
    setFormOpen(true);
  };

  const onClickDelete = (row: INavigationAuditRegexResponse) => {
    setDeleteTarget(row);
  };

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await NavigationAuditRegexService.remove(deleteTarget.navigation_id, deleteTarget.id);
      dispatch(removeMapping(deleteTarget.id));
      dispatch(showNotification({ message: 'Regex mapping deleted.', severity: 'success' }));
    } catch (e: any) {
      dispatch(
        showNotification({
          message: e?.response?.data?.message || 'Failed to delete.',
          severity: 'error',
        }),
      );
    } finally {
      setDeleteLoading(false);
      setDeleteTarget(null);
    }
  };

  const onSaved = (result: INavigationAuditRegexResponse) => {
    if (editTarget) {
      dispatch(updateMapping(result));
      dispatch(showNotification({ message: 'Regex mapping updated.', severity: 'success' }));
    } else {
      dispatch(addMapping(result));
      dispatch(showNotification({ message: 'Regex mapping created.', severity: 'success' }));
    }
    setEditTarget(null);
  };

  const columns: GridColDef[] = [
    { field: 'navigation_full_path', headerName: 'Navigation', flex: 1.5 },
    {
      field: 'url_regex',
      headerName: 'URL Regex',
      flex: 2,
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
          {value}
        </Typography>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1.5,
      valueFormatter: (v: string | null) => v ?? '—',
    },
    {
      field: 'created_by',
      headerName: 'Created By',
      width: 130,
      valueFormatter: (v: string | null) => v ?? '—',
    },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 160,
      valueFormatter: (v: string) => (v ? new Date(v).toLocaleString() : '—'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onClickEdit(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => onClickDelete(row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <>
      <CommonDatagrid
        data={mappings}
        columns={columns}
        rowId="id"
        headerActionButtons={
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              style={{ fontSize: 12, fontWeight: 600 }}
              onClick={() => {
                setEditTarget(null);
                setFormOpen(true);
              }}
            >
              Add Mapping
            </Button>
          </Box>
        }
      />

      <NavigationAuditRegexFormDialog
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditTarget(null);
        }}
        editTarget={editTarget}
        navigations={navigations}
        onSaved={onSaved}
      />

      <CommonDialog
        isOpen={!!deleteTarget}
        header={<CommonDialogHeader title="Confirm Delete" onClose={() => setDeleteTarget(null)} />}
        body={
          <Typography variant="body2" mt={1}>
            Are you sure you want to delete the regex mapping for{' '}
            <strong>{deleteTarget?.navigation_full_path}</strong>?
          </Typography>
        }
        footer={
          <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={onConfirmDelete}
              disabled={deleteLoading}
            >
              Delete
            </Button>
          </Box>
        }
      />
    </>
  );
};
